// Cave84 — Vintages service (Phase 1 Week 3 → updated Week 4)
// Vintages are created both by the import script and by the UI (AddBottlePage).
// A Vintage is the junction between a Wine and its physical year of production.

import {
  addDoc,
  doc,
  getDoc,
  getDocs,
  increment,
  query,
  updateDoc,
  where
} from 'firebase/firestore';
import type { Vintage } from '@cave84/shared-types';
import { db } from './firebase';
import { typedCollection } from './firestore';
import { createQueueEntry } from './generationQueue';

const COLLECTION = 'vintages';

function col() {
  return typedCollection<Vintage>(COLLECTION);
}

// ---------------------------------------------------------------------------
// Input type for UI-created vintages (import script sets all fields)
// ---------------------------------------------------------------------------

export type VintageCreateInput = {
  wine_id: string;
  year: number;
  vintage_quality_rating: Vintage['vintage_quality_rating'];
  drinking_window_initial: Vintage['drinking_window_initial'];
  drinking_window_current: Vintage['drinking_window_current'];
};

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export async function listVintagesByWine(wineId: string): Promise<Vintage[]> {
  const snap = await getDocs(
    query(col(), where('wine_id', '==', wineId))
  );
  // Sort client-side — avoids composite index requirement (where + orderBy on different fields)
  return snap.docs
    .map((d) => d.data())
    .sort((a, b) => b.year - a.year);
}

export async function getVintage(id: string): Promise<Vintage | null> {
  const snap = await getDoc(doc(db, COLLECTION, id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...(snap.data() as Omit<Vintage, 'id'>) };
}

/**
 * Find an existing vintage by wine + year — used in AddBottlePage to avoid dupes.
 * Returns null if none exists yet.
 */
export async function findVintageByWineAndYear(
  wineId: string,
  year: number
): Promise<Vintage | null> {
  const snap = await getDocs(
    query(col(), where('wine_id', '==', wineId), where('year', '==', year))
  );
  if (snap.empty) return null;
  return snap.docs[0]!.data();
}

// ---------------------------------------------------------------------------
// Create — called from UI when user adds a bottle for a year that has no Vintage yet
// ---------------------------------------------------------------------------

export async function createVintage(input: VintageCreateInput): Promise<Vintage> {
  const payload: Omit<Vintage, 'id'> = {
    wine_id: input.wine_id,
    year: input.year,
    vintage_quality_rating: input.vintage_quality_rating,
    drinking_window_initial: input.drinking_window_initial,
    drinking_window_current: input.drinking_window_current,
    drinking_window_revisions: [],
    last_known_drinking_status: 'developing', // sane default; Claude analysis will refine
    bottle_count: 0,
    price_last_refreshed_at: null,            // explicitly null — triggers "no price" panel
  };
  const ref = await addDoc(col(), payload as unknown as Vintage);
  // Increment denormalized counter on the parent Wine document
  await updateDoc(doc(db, 'wines', input.wine_id), { vintage_count: increment(1) });

  // Auto-queue a high-priority price lookup for the new vintage
  await createQueueEntry('market_price_refresh', ref.id, 'high');
  // Auto-queue a high-priority critic-score lookup for the new vintage
  await createQueueEntry('critic_scores', ref.id, 'high');

  return { id: ref.id, ...payload };
}

/**
 * Load all vintages — used for client-side join in InventoryPage.
 * ~230 docs; acceptable for client-side join with TanStack Query caching.
 */
export async function listAllVintages(): Promise<Vintage[]> {
  const snap = await getDocs(col());
  return snap.docs.map((d) => d.data());
}
